import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { Event } from '@fullstory/proto/fs/api/segment';
import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import {
  Box,
  Flex,
  CollapsibleSection,
  Banner,
  InputDescription,
  InputLabel,
  Pill,
  Text,
  TextArea,
  TextInput,
  Toggle,
  Tooltip,
  Icon,
  SelectMenu,
  OptionType,
  Checkbox,
  Divider,
  Spinner,
  useSelectMenu,
  IconButton,
  Popover,
} from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import {
  ElementPropertyRuleErrors,
  ElementPropertyRuleModalVariant,
} from '../ElementPropertyRuleModal';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  AvailableValues,
  Element,
  ElementPropertyRuleElement,
  ElementPropertyRuleSource,
} from '../ElementPropertyRuleModal/ElementPropertyRuleDetailsModal';
import {
  ActiveIndexContext,
  AppearancesContext,
  AppearancesContextProps,
  PreviewContext,
  PreviewState,
} from '@fullstory/signal-studio-context';
import { AvailableRawValues, ElementPropertyRuleConfig } from '../../types';
import { getSampleValues, makePlatformSelectorDeps, transformedValues } from '../../utils';
import * as vars from '@fullstory/proto/fs/api/vars';
import { prettifyVarType, VarType } from '@fullstory/search-vars';
import { useFlags } from '@fullstory/react-hooks';
import { multiValueInfo } from '@fullstory/util/vars';
import { useAiPropertyLabelSuggestions } from '../../hooks/useAiPropertyLabelSuggestions';

interface ActiveIndexProps {
  index: number;
  setIndex: (index: number) => void;
  deleteIndex: (index: number) => void;
}

export interface Warnings {
  element?: string[];
  parent?: string[];
  value?: string[];
  regex?: string[];
  propertyName?: string[];
  propertyType?: string[];
  privacy?: string[];
}

interface SidebarElementPropertyRuleProps {
  elementPropertyRule: ElementPropertyRuleConfig;
  archived: boolean;
  onChange: (elementPropertyRule: ElementPropertyRuleConfig) => void;
  availableValues?: AvailableRawValues;
  variant: ElementPropertyRuleModalVariant;
  errors: ElementPropertyRuleErrors;
  visibleElements?: NamedElementRecord[];
  omitNonVisibleElements?: boolean;
  attachedElements?: NamedElementRecord[];
  activeIndex: ActiveIndexProps;
  parentActiveIndex?: ActiveIndexProps;
  trimTrigger: boolean;
  setTrimTrigger: (trim: boolean) => void;
  warnings?: Warnings;
  definitionElementName?: string;
  sidebarV2Enabled?: boolean;
}

export const SidebarElementPropertyRule: React.FC<SidebarElementPropertyRuleProps> = ({
  elementPropertyRule,
  archived,
  onChange,
  availableValues,
  variant,
  errors,
  visibleElements,
  omitNonVisibleElements,
  attachedElements,
  activeIndex,
  parentActiveIndex,
  trimTrigger,
  setTrimTrigger,
  warnings: _warnings,
  definitionElementName,
  sidebarV2Enabled,
}) => {
  const showDividers = sidebarV2Enabled;
  const useSharePropertySection = sidebarV2Enabled;
  const disabled = archived;

  const previewContext = useContext(PreviewContext);
  // only want to show warnings when the preview is active
  // there are a handful of warnings that aren't relevant until we have some sample values
  const warnings = previewContext.state === PreviewState.ACTIVE ? _warnings : undefined;
  const availableValuesLoading = previewContext.state === PreviewState.LOADING;

  const header = useMemo(() => {
    switch (variant) {
      case ElementPropertyRuleModalVariant.new:
        return <FormattedMessage defaultMessage="Create Property Rule" id="e1M5dH" />;
      case ElementPropertyRuleModalVariant.view:
        return <FormattedMessage defaultMessage="View Property Rule" id="/C8o0N" />;
      default:
        assertExhaustive(variant);
    }
  }, [variant]);

  const hasChildElement = useMemo(
    () =>
      !!elementPropertyRule.anyChild ||
      elementPropertyRule.childNamedElementId !== undefined ||
      !!elementPropertyRule.childSelectors,
    [elementPropertyRule],
  );

  const childAppearancesEvent = useCallback(
    (event: Event | null): Event | null => {
      if (!event) {
        return event;
      }
      const constrainedEvent = { ...event, deps: [...event.deps] };
      // add parent selector constraints (either named element or raw selectors) to all underlying metric events
      if (elementPropertyRule.namedElementId) {
        constrainedEvent.deps.push({
          element: { withElementId: { value: [elementPropertyRule.namedElementId], spec: [] } },
        });
      }
      elementPropertyRule.selectors?.selectors?.forEach(selector =>
        constrainedEvent.deps.push(...makePlatformSelectorDeps(selector)),
      );
      return constrainedEvent;
    },
    [hasChildElement, elementPropertyRule.namedElementId, elementPropertyRule.selectors],
  );

  const appearances = useContext(AppearancesContext);
  // the child selectors do not show appearances when there is a parent/child relationship
  // this is because the appearances metrics can be misleading due to the child not necessarily having interactions / as many as the parent
  const childAppearances = useMemo(
    (): AppearancesContextProps | null =>
      hasChildElement && appearances
        ? {
            ...appearances,
            Appearances: undefined,
            showPreview: appearances.showPreview,
            getMetricFn: ({ event, ...rest }) => {
              const constrainedEvent = childAppearancesEvent(event);
              return appearances.getMetricFn({ event: constrainedEvent, ...rest });
            },
            appearancesFn: ({ event, ...rest }) => {
              const constrainedEvent = childAppearancesEvent(event);
              return appearances.appearancesFn({ event: constrainedEvent, ...rest });
            },
          }
        : appearances,
    [appearances, hasChildElement, childAppearancesEvent],
  );

  const visibleElementIds = useMemo(
    () => visibleElements?.map(namedElementRecord => namedElementRecord.namedElement!.id),
    [visibleElements],
  );

  return (
    <Box data-component="SidebarElementPropertyRule">
      {(header || definitionElementName) && (
        <Box marginBottom={20}>
          <Flex column gap={16} alignItems="flex-start">
            {header && <Text appearance="heading1">{header}</Text>}
            {definitionElementName && (
              <Flex justifyContent="flex-start" alignItems="baseline" gap={4}>
                <Box flexShrink={0}>
                  <Text appearance="bodySmall" color="secondary">
                    <FormattedMessage defaultMessage="Define property rule for" id="wkZB+H" />
                  </Text>
                </Box>
                <ElementTaggedPill
                  name={definitionElementName}
                  variant="primary"
                  color="blue-lighter"
                />
              </Flex>
            )}
          </Flex>
        </Box>
      )}
      <Flex
        column
        reverse={variant === ElementPropertyRuleModalVariant.new}
        alignItems="stretch"
        gap={showDividers ? 16 : 32}
      >
        <Flex column gap={16} alignItems="stretch">
          <NameAndDescription
            values={elementPropertyRule}
            setValues={onChange}
            errors={errors}
            disabled={disabled}
            sidebarV2Enabled={sidebarV2Enabled}
            availableSampleValues={availableValues}
          />
          <Banner
            variant="warning"
            description={
              variant === ElementPropertyRuleModalVariant.new ? (
                <FormattedMessage
                  defaultMessage="Please note that property rules are applied to future sessions."
                  id="iZU5R+"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Please note that property rule changes are applied to future sessions."
                  id="rVGOO5"
                />
              )
            }
          />
        </Flex>
        {showDividers && <Divider />}
        <Box display="flex" flexDirection="column" gap={showDividers ? 16 : 32}>
          <ActiveIndexContext.Provider value={activeIndex}>
            <AppearancesContext.Provider value={childAppearances}>
              <Definition
                availableValues={availableValues}
                availableValuesLoading={availableValuesLoading}
                values={elementPropertyRule}
                setValues={onChange}
                errors={errors}
                disabled={disabled}
                promotedElements={visibleElementIds}
                omitNonPromotedElements={omitNonVisibleElements}
                trimTrigger={trimTrigger}
                setTrimTrigger={setTrimTrigger}
                elementWarnings={warnings?.element}
                valueWarnings={warnings?.value}
                regexWarnings={warnings?.regex}
                propertyNameWarnings={warnings?.propertyName}
                propertyTypeWarnings={warnings?.propertyType}
                privacyWarnings={warnings?.privacy}
                hasChildElement={hasChildElement}
                sidebarV2Enabled={sidebarV2Enabled}
              />
            </AppearancesContext.Provider>
          </ActiveIndexContext.Provider>
          {showDividers && <Divider />}
          {useSharePropertySection ? (
            <SharedPropertyElements
              attachedElements={attachedElements}
              loading={availableValuesLoading}
            />
          ) : (
            parentActiveIndex && (
              <Flex column gap={32} alignItems="stretch">
                <ActiveIndexContext.Provider value={parentActiveIndex}>
                  <ParentContainer
                    values={elementPropertyRule}
                    setValues={onChange}
                    errors={errors}
                    disabled={disabled}
                    hasChildElement={hasChildElement}
                    promotedElements={visibleElementIds}
                    trimTrigger={trimTrigger}
                    setTrimTrigger={setTrimTrigger}
                    parentWarnings={warnings?.parent}
                  />
                </ActiveIndexContext.Provider>
                <AttachedElements
                  isNewElement={!elementPropertyRule.id}
                  attachedElements={attachedElements}
                />
              </Flex>
            )
          )}
        </Box>
      </Flex>
    </Box>
  );
};

interface CommonProps {
  values: ElementPropertyRuleConfig;
  setValues: (values: ElementPropertyRuleConfig) => void;
  errors: ElementPropertyRuleErrors;
  disabled: boolean;
}

interface DefinitionProps extends CommonProps {
  availableValues?: AvailableRawValues;
  availableValuesLoading?: boolean;
  promotedElements?: string[];
  omitNonPromotedElements?: boolean;
  trimTrigger: boolean;
  setTrimTrigger: (trim: boolean) => void;
  elementWarnings?: string[];
  valueWarnings?: string[];
  regexWarnings?: string[];
  propertyNameWarnings?: string[];
  propertyTypeWarnings?: string[];
  privacyWarnings?: string[];
  hasChildElement: boolean;
  sidebarV2Enabled?: boolean;
}

const Definition: React.FC<DefinitionProps> = ({
  availableValues,
  availableValuesLoading,
  values,
  setValues,
  errors,
  disabled,
  promotedElements,
  omitNonPromotedElements,
  trimTrigger,
  setTrimTrigger,
  elementWarnings,
  valueWarnings,
  regexWarnings,
  propertyNameWarnings,
  propertyTypeWarnings,
  privacyWarnings,
  hasChildElement,
  sidebarV2Enabled,
}) => {
  const rawSampleValues = useMemo(
    () => getSampleValues(values, availableValues),
    [values, availableValues],
  );
  const sampleValues = useMemo(
    () => transformedValues(values.regex, rawSampleValues),
    [values.regex, rawSampleValues],
  );

  return (
    <Flex data-component="Definition" column alignItems="stretch" gap={16}>
      <Box>
        <Text appearance="bodyMedium" color="emphasized">
          <FormattedMessage defaultMessage="Property Rule Definition" id="DNZPyF" />
        </Text>
        <Text appearance="bodyMedium" color="secondary">
          <FormattedMessage
            defaultMessage="Select from preview, type selector, or select an existing element"
            id="dVJtuw"
          />
        </Text>
      </Box>
      <DefinitionElement
        values={values}
        setValues={setValues}
        errors={errors}
        disabled={disabled}
        promotedElements={promotedElements}
        omitNonPromotedElements={!!omitNonPromotedElements && !availableValuesLoading}
        trimTrigger={trimTrigger}
        setTrimTrigger={setTrimTrigger}
      />
      {elementWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
      {privacyWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
      <DefinitionValue
        availableValues={availableValues}
        availableValuesLoading={availableValuesLoading}
        values={values}
        setValues={setValues}
        errors={errors}
        disabled={disabled}
      />
      {valueWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
      <DefinitionValueProducer
        values={values}
        setValues={setValues}
        errors={errors}
        disabled={disabled}
      />
      {regexWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
      {sampleValues && (
        <AvailableValues
          title={
            values.regex
              ? intl.formatMessage({ defaultMessage: 'Sample transformed values', id: 'FWVcL7' })
              : intl.formatMessage({ defaultMessage: 'Sample values', id: 'bqUvep' })
          }
          values={sampleValues}
          valuesLoading={availableValuesLoading}
          emptyMessage={intl.formatMessage({
            defaultMessage: 'No sample values available',
            id: 'WzgXgS',
          })}
        />
      )}
      {!sidebarV2Enabled && (
        <DefinitionPropertyName
          values={values}
          setValues={setValues}
          errors={errors}
          disabled={disabled}
        />
      )}
      {propertyNameWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
      <DefinitionPropertyType
        values={values}
        setValues={setValues}
        errors={errors}
        disabled={disabled}
        hasChildElement={hasChildElement}
      />
      {propertyTypeWarnings?.map((description, index) => (
        <Banner key={index} variant="warning" description={description} />
      ))}
    </Flex>
  );
};

interface DefinitionElementProps extends CommonProps {
  promotedElements?: string[];
  omitNonPromotedElements?: boolean;
  trimTrigger: boolean;
  setTrimTrigger: (trim: boolean) => void;
}

const DefinitionElement: React.FC<DefinitionElementProps> = ({
  values,
  setValues,
  errors: _errors,
  disabled,
  promotedElements,
  omitNonPromotedElements,
  trimTrigger,
  setTrimTrigger,
}) => {
  const { element, errors, isChild } = useMemo(() => {
    if (values.anyChild || values.childNamedElementId !== undefined || values.childSelectors) {
      return {
        element: {
          any: values.anyChild,
          namedElementId: values.childNamedElementId,
          selectors: values.childSelectors,
        },
        errors: {
          any: _errors.anyChild,
          namedElementId: _errors.childNamedElementId,
          selectors: _errors.childSelectors,
        },
        isChild: true,
      };
    }
    return {
      element: {
        any: values.any,
        namedElementId: values.namedElementId,
        selectors: values.selectors,
      },
      errors: {
        any: _errors.any,
        namedElementId: _errors.namedElementId,
        selectors: _errors.selectors,
      },
      isChild: false,
    };
  }, [values, _errors]);

  const onChange = useCallback(
    (element: Element) => {
      if (isChild) {
        // unset oneof fields as we are only going to set one of them based on the update
        const { anyChild, childNamedElementId, childSelectors, ...unsetValues } = values;
        const newValues: ElementPropertyRuleConfig = unsetValues;
        if (element.any) {
          newValues.anyChild = element.any;
        } else if (element.namedElementId !== undefined) {
          newValues.childNamedElementId = element.namedElementId;
        } else if (element.selectors) {
          newValues.childSelectors = element.selectors;
        }
        setValues(newValues);
      } else {
        // unset oneof fields as we are only going to set one of them based on the update
        const { any, namedElementId, selectors, ...unsetValues } = values;
        const newValues: ElementPropertyRuleConfig = unsetValues;
        if (element.any) {
          newValues.any = element.any;
        } else if (element.namedElementId !== undefined) {
          newValues.namedElementId = element.namedElementId;
        } else if (element.selectors) {
          newValues.selectors = element.selectors;
        }
        setValues(newValues);
      }
    },
    [isChild, values, setValues],
  );

  return (
    <ElementPropertyRuleElement
      data-component="DefinitionElement"
      element={element}
      onChange={onChange}
      errors={errors}
      disabled={disabled}
      promotedElements={promotedElements}
      omitNonPromotedElements={omitNonPromotedElements}
      smallDropdowns
      trimTrigger={trimTrigger}
      setTrimTrigger={setTrimTrigger}
      preventOverflow
    />
  );
};

interface DefinitionValueProps extends CommonProps {
  availableValues?: AvailableRawValues;
  availableValuesLoading?: boolean;
}

const DefinitionValue: React.FC<DefinitionValueProps> = ({
  availableValues,
  availableValuesLoading,
  values,
  setValues,
  errors,
  disabled,
}) => (
  <Box data-component="DefinitionValue">
    <InputLabel id="value">
      <FormattedMessage defaultMessage="Value" id="GufXy5" />
    </InputLabel>
    <ElementPropertyRuleSource
      labeledBy="value"
      availableValues={availableValues}
      availableValuesLoading={availableValuesLoading}
      source={values}
      onChange={source => {
        const updatedElementPropertyRule = { ...values };
        // have to delete all one of values first to ensure only one is set
        delete updatedElementPropertyRule.attribute;
        delete updatedElementPropertyRule.innerText;
        delete updatedElementPropertyRule.static;
        if (source.attribute) {
          updatedElementPropertyRule.attribute = source.attribute;
        } else if (source.innerText) {
          updatedElementPropertyRule.innerText = source.innerText;
        } else if (source.static) {
          updatedElementPropertyRule.static = source.static;
        }
        setValues(updatedElementPropertyRule);
      }}
      errors={errors}
      valueRegex={!!values.regex}
      setValueRegex={enabled => {
        if (enabled) {
          setValues({ ...values, regex: { expression: '', format: '' } });
        } else {
          const updatedElementPropertyRule = { ...values };
          delete updatedElementPropertyRule.regex;
          setValues(updatedElementPropertyRule);
        }
      }}
      disabledOptions={values.any || values.anyChild}
      disabled={disabled}
      smallDropdowns
      preventOverflow
    />
  </Box>
);

interface DefinitionPropertyNameProps extends CommonProps {
  availableSampleValues?: AvailableRawValues;
}

const DefinitionPropertyName: React.FC<DefinitionPropertyNameProps> = ({
  values,
  setValues,
  errors,
  disabled,
  availableSampleValues,
}) => {
  const flags = useFlags();
  const elementPropertyRulesV2Enabled = flags.featureFlags().AppElementPropertyRulesV2;
  const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
  const {
    suggestions: options,
    loading,
    loadSuggestions,
    isEnabled: aiLabelsEnabled,
  } = useAiPropertyLabelSuggestions({
    namedElementId: values.namedElementId || '',
    propertyType: values.propertyType,
    source: {
      attribute: values.attribute,
      innerText: values.innerText,
      static: values.static,
    },
    sampleValues: (getSampleValues(values, availableSampleValues)?.values as string[]) || [],
    propertyRuleDescription: values.description,
  });

  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(null);
  const { Options: OptionsAi, optionsProps: optionsPropsAi } = useSelectMenu({
    testId: 'property-modal-name-suggestions-ai',
    options,
    onChange: option => {
      if (option && option.label) {
        setValues({
          ...values,
          propertyName: option.label,
          description: values.description || option.value.description, // Only use suggested description if we don't have one.
        });
        setSuggestionsOpen(false);
      }
    },
  });
  return (
    <Box data-component="DefinitionPropertyName" ref={setPopoverAnchor}>
      <InputLabel id="property">
        <FormattedMessage defaultMessage="Property Name" id="G9Im6D" />
      </InputLabel>
      <TextInput
        onChange={e => {
          setValues({ ...values, propertyName: e.target.value });
        }}
        value={values.propertyName}
        placeholder={intl.formatMessage({
          defaultMessage: 'e.g. productName',
          id: '9jLB7X',
        })}
        pattern="[a-z]"
        disabled={disabled}
        invalid={errors.propertyName !== undefined}
        aria-labelledby="property"
        testId="element-property-rule-modal-property-name-input"
        rightAdornment={
          aiLabelsEnabled && elementPropertyRulesV2Enabled ? (
            <IconButton
              iconColor="ai"
              size="large"
              title={intl.formatMessage({ defaultMessage: 'Suggest name', id: 'KHtUXF' })}
              iconName={loading ? 'ai-animated' : 'ai'}
              onClick={() => {
                loadSuggestions();
                setSuggestionsOpen(true);
              }}
            />
          ) : undefined
        }
      />
      {errors.propertyName && <InputDescription invalid description={errors.propertyName} />}
      {aiLabelsEnabled && elementPropertyRulesV2Enabled && (
        <Popover
          anchor={popoverAnchor}
          onCloseSignal={() => setSuggestionsOpen(false)}
          open={suggestionsOpen}
          variant="bordered"
          testId="property-modal-name-suggestions"
        >
          <Box maxHeight={385} overflow="auto">
            <OptionsAi {...optionsPropsAi} />
            <Divider />
          </Box>
        </Popover>
      )}
    </Box>
  );
};

const propertyTypeOptions: OptionType<vars.Type>[] = [
  {
    key: 'str',
    label: prettifyVarType(VarType.Str),
    value: vars.Type.str,
  },
  {
    key: 'real',
    label: prettifyVarType(VarType.Real),
    value: vars.Type.real,
  },
  {
    key: 'int',
    label: prettifyVarType(VarType.Int),
    value: vars.Type.int,
  },
  {
    key: 'bool',
    label: prettifyVarType(VarType.Bool),
    value: vars.Type.bool,
  },
  {
    key: 'date',
    label: prettifyVarType(VarType.Date),
    value: vars.Type.date,
  },
];

type DefinitionPropertyTypeProps = CommonProps & {
  hasChildElement: boolean;
};

const DefinitionPropertyType: React.FC<DefinitionPropertyTypeProps> = ({
  values,
  setValues,
  errors,
  disabled,
  hasChildElement,
}) => {
  const flags = useFlags();
  const { single, multi, isMulti } = multiValueInfo(values.propertyType);
  const multiNotInvalid = multi !== vars.Type.INVALID_TYPE ? multi : vars.Type.strs;

  const MultiValueCheckbox = (
    <Checkbox
      checked={isMulti}
      onChange={checked =>
        setValues({ ...values, propertyType: checked ? multiNotInvalid : single })
      }
      label={intl.formatMessage({
        defaultMessage: 'Capture multiple values',
        id: 'UTKY6c',
      })}
      disabled={disabled || !hasChildElement}
    />
  );

  return (
    <Box data-component="DefinitionPropertyType">
      <InputLabel id="propertyType">
        <FormattedMessage defaultMessage="Property Type" id="FgYFTM" />
      </InputLabel>
      <Flex justifyContent="flex-start" alignItems="baseline" gap={8}>
        <SelectMenu
          placeholder={intl.formatMessage({ defaultMessage: 'Select a type...', id: 'vJ5S2P' })}
          options={propertyTypeOptions}
          selected={propertyTypeOptions.find(opt => opt.value === single)}
          onChange={opt => {
            const { single, multi } = multiValueInfo(opt?.value ?? vars.Type.INVALID_TYPE);
            // we need to preserve the single or multi form based on if capture multiple is checked
            setValues({ ...values, propertyType: isMulti ? multi : single });
          }}
          clearable
          disabled={disabled}
          aria-labelledby="propertyType"
          testId="element-property-rule-modal-property-type-select"
          variant="fill-builder"
          preventOverflow={16}
          flipY
        />
        {flags.featureFlags().AppMultiValueExtractedProperties && (
          <Box flexShrink={0}>
            {hasChildElement ? (
              MultiValueCheckbox
            ) : (
              <Tooltip
                tooltipContent={intl.formatMessage({
                  defaultMessage: 'Parent required to capture multiple child values.',
                  id: 'Rj0z8+',
                })}
              >
                {MultiValueCheckbox}
              </Tooltip>
            )}
          </Box>
        )}
      </Flex>
      {errors.propertyType && <InputDescription invalid description={errors.propertyType} />}
      <Box marginTop={8}>
        <Text appearance="bodySmall" color="secondary">
          <FormattedMessage
            defaultMessage="Type determines what kind of calculations are able to be performed with the property."
            id="1PVpA9"
          />
        </Text>
      </Box>
    </Box>
  );
};

const DefinitionValueProducer: React.FC<CommonProps> = ({
  values,
  setValues,
  errors,
  disabled,
}) => {
  const regex = values.regex;
  if (!regex) {
    return null;
  }
  return (
    <Box data-component="DefinitionValueProducer" display="flex" flexDirection="column" gap={16}>
      <Box display="flex" gap={8}>
        <Box width="60%">
          <Box display="flex" alignItems="center" gap={4} marginBottom={4}>
            <InputLabel id="regex" disableMargin>
              <FormattedMessage defaultMessage="Regex" id="vrHftl" />
            </InputLabel>
            <Tooltip tooltipContent={regexTooltipContent()}>
              <Icon name="information" />
            </Tooltip>
          </Box>
          <TextInput
            onChange={e => {
              setValues({ ...values, regex: { ...regex, expression: e.target.value } });
            }}
            value={regex.expression}
            placeholder={intl.formatMessage(
              {
                defaultMessage: 'e.g. ({currencySymbol})({amount})',
                id: 'cz/js9',
              },
              { currencySymbol: '\\$', amount: '\\d+\\.\\d{2}' },
            )}
            disabled={disabled}
            invalid={errors.regex?.expression !== undefined}
            aria-labelledby="regex"
            testId="element-property-rule-modal-regex-expression-input"
          />
          {errors.regex?.expression && (
            <InputDescription invalid description={errors.regex.expression} />
          )}
        </Box>
        <Box>
          <Box display="flex" alignItems="center" gap={4} marginBottom={4}>
            <InputLabel id="regexFormat" optional disableMargin>
              <FormattedMessage defaultMessage="Format" id="zQLEfz" />
            </InputLabel>
            <Tooltip tooltipContent={regexFormatTooltipContent()}>
              <Icon name="information" />
            </Tooltip>
          </Box>
          <TextInput
            onChange={e => {
              setValues({ ...values, regex: { ...regex, format: e.target.value } });
            }}
            value={regex.format}
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. (1)(2)',
              id: 'ts3H0r',
            })}
            disabled={disabled}
            invalid={errors.regex?.format !== undefined}
            aria-labelledby="regexFormat"
            testId="element-property-rule-modal-regex-format-input"
          />
          {errors.regex?.format && <InputDescription invalid description={errors.regex.format} />}
        </Box>
      </Box>
    </Box>
  );
};

const regexTooltipContent = () => (
  <Box data-component="regexTooltipContent">
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Use regex to extract a specific value from a given string"
        id="TUvIpN"
      />
    </Text>
    <br />
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage defaultMessage="Example" id="xrCRnH" />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="String: {string}"
        id="1T9yQM"
        values={{
          string: '$3,500.99',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Regex: {regex}"
        id="pArZVu"
        values={{
          regex: '^\\$(?:(\\d{1,3}),)?(\\d{1,3})(\\.\\d{2})?$',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Transformed value: {value}"
        id="0dojNU"
        values={{
          value: '3500.99',
        }}
      />
    </Text>
  </Box>
);

const regexFormatTooltipContent = () => (
  <Box data-component="regexTooltipContent">
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Use format to arrange captured values and apply additional formatting to produce structured values. <a>Learn more</a>"
        id="PLSMlt"
        values={{
          a: (text: ReactNode) => (
            <a
              href="https://help.fullstory.com/hc/en-us/articles/24236341468311-Extracted-Element-Properties"
              target="_blank"
              rel="noreferrer"
            >
              {text}
            </a>
          ),
        }}
      />
    </Text>
    <br />
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage defaultMessage="Example" id="xrCRnH" />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="String: {string}"
        id="1T9yQM"
        values={{
          string: '100,25 €',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Regex: {regex}"
        id="pArZVu"
        values={{
          regex: '^(?:(\\d{1,3}).)??(\\d{1,3})??(?:,(\\d{2}))? €$',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Transformed value: {value}"
        id="0dojNU"
        values={{
          value: '100,25',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Format: {format}"
        id="Yj1hDB"
        values={{
          format: '(1)(2).(3?00)',
        }}
      />
    </Text>
    <Text color="default-inverse" appearance="bodySmall">
      <FormattedMessage
        defaultMessage="Transformed value: {value}"
        id="0dojNU"
        values={{
          value: '100.25',
        }}
      />
    </Text>
  </Box>
);

interface SharePropertyProps {
  attachedElements?: NamedElementRecord[];
  loading: boolean;
}

const SharedPropertyElements: React.FC<SharePropertyProps> = ({ attachedElements, loading }) => (
  <Flex data-component="ShareProperty" column alignItems="stretch" gap={16}>
    <Box>
      <Text appearance="bodyMedium" color="emphasized">
        <FormattedMessage defaultMessage="Shared Property Elements" id="WVD9/B" />
      </Text>
      <Text appearance="bodyMedium" color="secondary">
        <FormattedMessage
          defaultMessage="Elements that will share the property rule data."
          id="oxQ/gO"
        />
      </Text>
    </Box>
    {loading ? (
      <Spinner />
    ) : attachedElements?.length ? (
      <Flex gap={4} justifyContent="flex-start" flexWrap="wrap">
        {attachedElements.map(namedElementRecord => (
          <ElementTaggedPill
            key={namedElementRecord.namedElement?.id}
            name={namedElementRecord.namedElement?.name ?? ''}
            variant="primary"
            color="secondary"
          />
        ))}
      </Flex>
    ) : (
      <Text appearance="bodySmall" color="disabled" style={{ fontStyle: 'italic' }}>
        <FormattedMessage defaultMessage="No shared property elements" id="+GIy/A" />
      </Text>
    )}
  </Flex>
);

interface ParentContainerProps extends CommonProps {
  hasChildElement: boolean;
  promotedElements?: string[];
  trimTrigger: boolean;
  setTrimTrigger: (trim: boolean) => void;
  parentWarnings?: string[];
}

const ParentContainer: React.FC<ParentContainerProps> = ({
  values,
  setValues,
  errors,
  disabled,
  hasChildElement,
  promotedElements,
  trimTrigger,
  setTrimTrigger,
  parentWarnings,
}) => {
  const activeIndex = useContext(ActiveIndexContext);

  return (
    <Box data-component="ParentContainer">
      <Box paddingBottom={8} display="flex" gap={16} alignItems="center">
        <Text appearance="bodyMediumBold" color={hasChildElement ? 'default' : 'secondary'}>
          <FormattedMessage defaultMessage="Parent Container" id="/Xo+kw" />
        </Text>
        <Toggle
          checked={hasChildElement}
          disabled={disabled}
          onToggle={val => {
            const updatedElementPropertyRule = { ...values };
            if (val) {
              // If this is set, we should update the original values to be child values.
              // Also, default to parent element selection.
              updatedElementPropertyRule.anyChild = values.any;
              updatedElementPropertyRule.childNamedElementId = values.namedElementId;
              updatedElementPropertyRule.childSelectors = values.selectors;
              delete updatedElementPropertyRule.any;
              delete updatedElementPropertyRule.selectors;
              updatedElementPropertyRule.namedElementId = '';
              // and when toggled on, make the new option active by default
              activeIndex?.setIndex(0);
            } else {
              // If this is unset, we should update the child values to be the original values.
              updatedElementPropertyRule.any = values.anyChild;
              updatedElementPropertyRule.namedElementId = values.childNamedElementId;
              updatedElementPropertyRule.selectors = values.childSelectors;
              delete updatedElementPropertyRule.anyChild;
              delete updatedElementPropertyRule.childNamedElementId;
              delete updatedElementPropertyRule.childSelectors;
            }
            setValues(updatedElementPropertyRule);
          }}
        />
      </Box>
      <Box>
        <Text appearance="bodySmall" color="secondary">
          <FormattedMessage
            defaultMessage="Properties are attached to Elements, either directly, or by selecting an optional parent container."
            id="l49jba"
          />
        </Text>
      </Box>
      {hasChildElement && (
        <>
          <Box marginTop={20}>
            <ParentElement
              values={values}
              setValues={setValues}
              errors={errors}
              disabled={disabled}
              promotedElements={promotedElements}
              trimTrigger={trimTrigger}
              setTrimTrigger={setTrimTrigger}
            />
          </Box>
          {!!parentWarnings?.length && (
            <Box marginTop={16}>
              {parentWarnings.map((description, index) => (
                <Banner key={index} variant="warning" description={description} />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

interface ParentElementProps extends CommonProps {
  promotedElements?: string[];
  trimTrigger: boolean;
  setTrimTrigger: (trim: boolean) => void;
}

const ParentElement: React.FC<ParentElementProps> = ({
  values,
  setValues,
  errors,
  disabled,
  promotedElements,
  trimTrigger,
  setTrimTrigger,
}) => {
  const onChange = useCallback(
    (element: Element) => {
      // unset oneof fields as we are only going to set one of them based on the update
      const { any, namedElementId, selectors, ...unsetValues } = values;
      const newValues: ElementPropertyRuleConfig = unsetValues;
      if (element.any) {
        newValues.any = element.any;
      } else if (element.namedElementId !== undefined) {
        newValues.namedElementId = element.namedElementId;
      } else if (element.selectors) {
        newValues.selectors = element.selectors;
      }
      setValues(newValues);
    },
    [values, setValues],
  );

  return (
    <ElementPropertyRuleElement
      data-component="ParentElement"
      element={{
        any: values.any,
        namedElementId: values.namedElementId,
        selectors: values.selectors,
      }}
      onChange={onChange}
      errors={{
        any: errors.any,
        namedElementId: errors.namedElementId,
        selectors: errors.selectors,
      }}
      disabled={disabled}
      promotedElements={promotedElements}
      smallDropdowns
      trimTrigger={trimTrigger}
      setTrimTrigger={setTrimTrigger}
      preventOverflow
    />
  );
};

interface NameAndDescriptionProps extends CommonProps {
  sidebarV2Enabled?: boolean;
  availableSampleValues?: AvailableRawValues;
}

const NameAndDescription: React.FC<NameAndDescriptionProps> = ({
  values,
  setValues,
  errors,
  disabled,
  sidebarV2Enabled,
  availableSampleValues,
}) => (
  <Flex data-component="NameAndDescription" column alignItems="stretch" gap={16}>
    {sidebarV2Enabled && (
      <>
        <Flex column alignItems="stretch">
          <DefinitionPropertyName
            values={values}
            setValues={setValues}
            errors={errors}
            disabled={disabled}
            availableSampleValues={availableSampleValues}
          />
          <Checkbox
            label={intl.formatMessage({
              defaultMessage: 'Use the same name for the property rule',
              id: 'fPwDcQ',
            })}
            disabled={disabled}
            checked={values.usePropertyName ?? false}
            onChange={() => setValues({ ...values, usePropertyName: !values.usePropertyName })}
          />
        </Flex>
      </>
    )}
    <Box>
      <InputLabel id="name">
        <FormattedMessage defaultMessage="Property Rule Name" id="TpJpoe" />
      </InputLabel>
      <TextInput
        onChange={e => {
          setValues({ ...values, name: e.target.value });
        }}
        value={values.name}
        placeholder={intl.formatMessage({
          defaultMessage: 'e.g. Product Name',
          id: 'JsEH4p',
        })}
        disabled={values.usePropertyName || disabled}
        invalid={errors.name !== undefined}
        aria-labelledby="name"
        testId="element-property-rule-modal-name-input"
      />
      {errors.name && <InputDescription invalid description={errors.name} />}
    </Box>
    <Box>
      <Flex justifyContent="flex-start" alignItems="baseline">
        <InputLabel id="description">
          <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
        </InputLabel>
        <Box margin={[0, 0, 4, 8]}>
          <Text appearance="bodyXSmall" color="secondary">
            <FormattedMessage defaultMessage="Optional" id="InWqys" />
          </Text>
        </Box>
      </Flex>
      <TextArea
        onChange={e => {
          setValues({ ...values, description: e.target.value });
        }}
        value={values.description}
        placeholder={intl.formatMessage({
          defaultMessage: 'Add a short description for this Property Rule...',
          id: 'CSDouM',
        })}
        disabled={disabled}
        aria-labelledby="description"
      />
    </Box>
  </Flex>
);

interface AttachedElementsProps {
  isNewElement: boolean;
  attachedElements?: NamedElementRecord[];
}

const AttachedElements: React.FC<AttachedElementsProps> = ({ isNewElement, attachedElements }) => {
  if (!attachedElements?.length) {
    return null;
  }
  return (
    <Box data-component="AttachedElements">
      <CollapsibleSection initiallyCollapsed={!isNewElement}>
        <Box paddingBottom={16}>
          <CollapsibleSection.Header>
            <Box marginRight={4}>
              <CollapsibleSection.Icon size={16} />
            </Box>
            <Text appearance="bodyMediumBold" color="default">
              <FormattedMessage defaultMessage="Attached Elements" id="P0Yc7b" />
            </Text>
          </CollapsibleSection.Header>
        </Box>
        <CollapsibleSection.Panel>
          {attachedElements.map(namedElementRecord => (
            <Box
              key={namedElementRecord.namedElement?.id}
              display="flex"
              gap={8}
              paddingBottom={8}
              alignItems="center"
            >
              <Pill variant="primary" noExternalSpacing>
                <Text appearance="bodyMediumBold">
                  <FormattedMessage defaultMessage="Element" id="SHkKGm" />
                </Text>
              </Pill>
              <Text appearance="bodyMedium" color="secondary">
                <FormattedMessage defaultMessage="is" id="fXdkiI" />
              </Text>
              <Pill noExternalSpacing variant="primary">
                <Text appearance="bodyMediumBold">{namedElementRecord.namedElement?.name}</Text>
              </Pill>
            </Box>
          ))}
        </CollapsibleSection.Panel>
      </CollapsibleSection>
    </Box>
  );
};

type ElementTaggedPillProps = Pick<React.ComponentProps<typeof Pill>, 'variant' | 'color'> & {
  name: string;
};

const ElementTaggedPill: React.FC<ElementTaggedPillProps> = ({ name, variant, color }) => (
  <Box data-component="ElementTaggedPill" overflow="hidden">
    <Pill variant={variant} color={color} noExternalSpacing>
      <Text appearance="bodySmall" noWrap>
        <FormattedMessage
          defaultMessage="<b>Element:</b> {name}"
          id="ArQ2TI"
          values={{
            name,
            b(text) {
              return <b>{text}</b>;
            },
          }}
        />
      </Text>
    </Pill>
  </Box>
);
